import React from "react";
import styled from "styled-components/macro";

import { useSelector } from "react-redux";

import { ConsignmentGroupByOption } from "constants/consignments";
import { NeverEverAccreditationStatus } from "constants/greenhams";

import {
  getNeverEverAccreditationStatusBySaleLotId,
  getSaleLotIdsByPenId,
  selectConsignmentIdsByReceivingPenGrouperLookup,
  selectConsignmentIdsByVendorGrouperLookup,
  selectConsignmentIdsByVendorNumberGrouperLookup,
  selectNeverEverAccreditationStatusBySaleLotIdLookup,
  selectQuantityBySaleLotIdLookup,
  selectSaleLotIdsByConsignmentIdLookup,
} from "selectors";

import NeverEverPng from "img/NeverEverLogo.png";

import { useIsExtraSmallMobile, useIsSmallMobile } from "hooks";

const NeverEverContainer = styled.div(
  ({ theme: { colors, radii, space } }) => `
    background-color: ${colors.neverEverBranding}10;
    border: 1px solid ${colors.controlBorder};
    border-radius: ${radii[3]}px;
    display: flex;
    padding: ${space[1]}px;
    align-items: center;
    justify-content: space-between; 
`,
);

export interface NeverEverStatusProps {
  totalCount: number;
  accreditedCount: number;
}

export function NeverEverStatus(
  props: NeverEverStatusProps,
): React.JSX.Element | null {
  const { totalCount, accreditedCount } = props;

  const isExtraSmallMobile = useIsExtraSmallMobile();
  const isSmallMobile = useIsSmallMobile();

  if (!(accreditedCount > 0)) {
    return null;
  }

  if (accreditedCount === totalCount) {
    return <img alt="Never Ever accredited" height={48} src={NeverEverPng} />;
  }

  return (
    <NeverEverContainer>
      <img
        alt="Partially Never Ever accredited"
        height={32}
        src={NeverEverPng}
      />
      <span className="p-2 font-medium whitespace-nowrap">
        {accreditedCount}
        {isExtraSmallMobile ? " / " : " of "}
        {totalCount}
        {isSmallMobile ? "" : " Hd"}
      </span>
    </NeverEverContainer>
  );
}

export interface NeverEverConsignmentGroupStatusProps {
  groupOption: ConsignmentGroupByOption;
  grouperValue: string;
}

const groupBySelectorMap = {
  [ConsignmentGroupByOption.VENDOR]: selectConsignmentIdsByVendorGrouperLookup,
  [ConsignmentGroupByOption.VENDOR_NUMBER]:
    selectConsignmentIdsByVendorNumberGrouperLookup,
  [ConsignmentGroupByOption.RECEIVING_PEN]:
    selectConsignmentIdsByReceivingPenGrouperLookup,
} satisfies Record<
  ConsignmentGroupByOption,
  (state: RootState) => Record<string, string[]>
>;

interface AccreditationCounts {
  accreditedCount: number;
  totalCount: number;
}

function getSaleLotAccreditationCounts(
  saleLotIds: string[],
  neverEverAccreditationStatusBySaleLotIdLookup: Record<
    string,
    NeverEverAccreditationStatus
  >,
  quantityBySaleLotIdLookup: Record<string, number>,
): AccreditationCounts {
  return saleLotIds.reduce<AccreditationCounts>(
    (acc, saleLotId) => {
      const accreditationStatus =
        neverEverAccreditationStatusBySaleLotIdLookup[saleLotId];
      const quantity = quantityBySaleLotIdLookup[saleLotId] || 0;

      acc.totalCount += quantity;
      if (accreditationStatus === NeverEverAccreditationStatus.ACCREDITED) {
        acc.accreditedCount += quantity;
      }

      return acc;
    },
    { totalCount: 0, accreditedCount: 0 },
  );
}

function getConsignmentAccreditationCounts(
  consignmentIds: string[],
  saleLotIdsByConsignmentIdLookup: Record<string, string[]>,
  neverEverAccreditationStatusBySaleLotIdLookup: Record<
    string,
    NeverEverAccreditationStatus
  >,
  quantityBySaleLotIdLookup: Record<string, number>,
): AccreditationCounts {
  // calculate the total quantity and accredited quantity of all the sale lots in all the consignments in this consignment group
  return consignmentIds.reduce<AccreditationCounts>(
    (acc, consignmentId) => {
      // Get the sale lot IDs for the current consignment ID
      const saleLotIds = saleLotIdsByConsignmentIdLookup[consignmentId] || [];

      const saleLotCounts = getSaleLotAccreditationCounts(
        saleLotIds,
        neverEverAccreditationStatusBySaleLotIdLookup,
        quantityBySaleLotIdLookup,
      );

      acc.accreditedCount += saleLotCounts.accreditedCount;
      acc.totalCount += saleLotCounts.totalCount;

      return acc;
    },
    { accreditedCount: 0, totalCount: 0 },
  );
}

export function NeverEverConsignmentGroupStatus(
  props: NeverEverConsignmentGroupStatusProps,
): React.JSX.Element | null {
  const { groupOption, grouperValue } = props;

  const consignmentIds: string[] =
    useSelector(
      state => groupBySelectorMap[groupOption](state)[grouperValue],
    ) || [];

  const saleLotIdsByConsignmentIdLookup = useSelector(
    selectSaleLotIdsByConsignmentIdLookup,
  );
  const neverEverAccreditationStatusBySaleLotIdLookup = useSelector(
    selectNeverEverAccreditationStatusBySaleLotIdLookup,
  );
  const quantityBySaleLotIdLookup: { [key: string]: number } = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux state type incomplete
    selectQuantityBySaleLotIdLookup as any,
  );

  const { accreditedCount, totalCount }: AccreditationCounts =
    getConsignmentAccreditationCounts(
      consignmentIds,
      saleLotIdsByConsignmentIdLookup,
      neverEverAccreditationStatusBySaleLotIdLookup,
      quantityBySaleLotIdLookup,
    );

  return (
    <NeverEverStatus
      accreditedCount={accreditedCount}
      totalCount={totalCount}
    />
  );
}

export interface NeverEverPenStatusProps {
  auctionPenId: string;
}

export function NeverEverAuctionPenStatus(
  props: NeverEverPenStatusProps,
): React.JSX.Element | null {
  const { auctionPenId } = props;

  const saleLotsIds: string[] =
    useSelector(getSaleLotIdsByPenId(auctionPenId)) || [];

  const neverEverAccreditationStatusBySaleLotIdLookup = useSelector(
    selectNeverEverAccreditationStatusBySaleLotIdLookup,
  );
  const quantityBySaleLotIdLookup: { [key: string]: number } = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Redux state type incomplete
    selectQuantityBySaleLotIdLookup as any,
  );

  const { accreditedCount, totalCount }: AccreditationCounts =
    getSaleLotAccreditationCounts(
      saleLotsIds,
      neverEverAccreditationStatusBySaleLotIdLookup,
      quantityBySaleLotIdLookup,
    );

  return (
    <NeverEverStatus
      accreditedCount={accreditedCount}
      totalCount={totalCount}
    />
  );
}

export interface NeverEverSaleLotStatusProps {
  saleLotId: string;
}

export function NeverEverSaleLotStatus(
  props: NeverEverSaleLotStatusProps,
): React.JSX.Element | null {
  const { saleLotId } = props;

  const accreditationStatus = useSelector(
    getNeverEverAccreditationStatusBySaleLotId(saleLotId),
  );

  const accreditedCount =
    accreditationStatus === NeverEverAccreditationStatus.ACCREDITED ? 1 : 0;
  const totalCount =
    accreditationStatus === NeverEverAccreditationStatus.ACCREDITED ? 1 : 0;

  return (
    <NeverEverStatus
      accreditedCount={accreditedCount}
      totalCount={totalCount}
    />
  );
}
